import { Factory } from "@/data/dao/Factory";
import { TableDao } from "@/data/dao/service/TableDao";
import { PermalinkMode } from "@/data/model/Enums";
import type { ITable } from "@/data/model/Table";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";

import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { BaseTabbedController } from "@/portal/controller/BaseTabbedController";
import { Select } from "@/portal/formControls/Select";
import { PermalinkGenerator } from "@/portal/forms/PermalinkGenerator";
import { TableForm } from "@/portal/forms/TableForm";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";

export class TableController extends BaseTabbedController<ITable> {
    private tableForm: TableForm;
    private permalinkGenerator?: PermalinkGenerator;

    constructor(element: JQuery) {
        super(element, Factory.getInstance(TableDao));

        this.tableForm = new TableForm(this.formContentByTabName("tableedit"));
        this.registerModelForm(this.tableForm);

        this.onActivateTab = (newTab) => {
            if (newTab === "tablelink") this.showPermaLink();
            else if (newTab === "debug") this.showDebugArea();
        };
    }

    protected onModelLoaded(m?: ITable) {
        if (m === undefined) {
            this.disableTabs(["tablelink", "debug"]);
            const vw = $("#kt_header > #kt_header_menu_wrapper");
            if (vw.find("> .selectedlocation").length === 0)
                vw.append(
                    '<div class="selectedlocation">' +
                        Format.htmlEscape(TastierGlobal.getLocationSwitch()!.getCurrentLocationName()) +
                        "</div>"
                );
        } else this.enableTabs(["tablelink", "debug"]);

        this.tableForm.setData(m);
    }

    private showPermaLink() {
        if (this.permalinkGenerator === undefined) {
            const $generator = this.formContentByTabName("tablelink").find(".permalinkgenerator");
            this.permalinkGenerator = new PermalinkGenerator($generator);

            const $modeGroup = $(
                '<div class="form-group row mt-4 ml-0"><label class="col-form-label">' +
                    i18n("table.permalink.mode") +
                    '</label><div class="col" /></div>'
            ).insertAfter($generator);

            const $modeSelect = $("<select />").appendTo($modeGroup.find("> .col"));
            Misc.eachEnum(PermalinkMode, (i, e) => {
                $modeSelect.append('<option value="' + i + '">' + i18n("enums.PermalinkMode." + e) + "</option>");
            });

            const modeSelect = Select.createFromSelect($modeSelect);
            modeSelect.setValue(PermalinkMode.None);
            modeSelect.onChange = (value) => this.permalinkGenerator!.setMode(parseInt(value!) as PermalinkMode);
        }

        this.permalinkGenerator.loadForTable(this.entityId!);
    }

    private showDebugArea() {
        const resetBtn = this.formContentByTabName("debug")
            .find("#resettablebtn")
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();

                resetBtn.addClass(style.buttonLoaderClass).prop("disabled", true);

                (this.dataSource as TableDao)
                    .reset(this.entityId!)
                    .then((m) => {
                        this.model = m;
                        this.onModelLoaded(m);
                        resetBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showSuccess(i18n("table.successReset"));
                    })
                    .catch((ex) => {
                        logError(ex);
                        resetBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showException(i18n("table.failedReset"));
                    });
            });

        const deallocateBtn = this.formContentByTabName("debug")
            .find("#deallocatebtn")
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();

                deallocateBtn.addClass(style.buttonLoaderClass).prop("disabled", true);

                (this.dataSource as TableDao)
                    .reset(this.entityId!)
                    .then(() => {
                        deallocateBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showSuccess(i18n("table.allUsersDeallocated"));
                    })
                    .catch((ex) => {
                        logError(ex);
                        deallocateBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showException(i18n("table.failedDeallocating"));
                    });
            });
    }
}
